
import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import MainNav from '@/components/MainNav';
import AccessibilityWidget from '@/components/AccessibilityWidget';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const SubscribePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = React.useState(false);
  
  // Get the plan from location state or attempt to get from localStorage
  const planFromState = location.state?.plan;
  const [plan, setPlan] = React.useState(planFromState);
  
  useEffect(() => {
    // Check if we have no plan from state but have subscription intent in localStorage
    if (!planFromState) {
      const savedIntent = localStorage.getItem('subscriptionIntent');
      if (savedIntent) {
        try {
          setPlan(JSON.parse(savedIntent));
        } catch (e) {
          console.error('Failed to parse saved subscription intent', e);
          navigate('/pricing');
        }
      } else {
        // No plan information available, redirect to pricing
        navigate('/pricing');
      }
    }
  }, [planFromState, navigate]);

  // If no plan and still loading, show loading state
  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const handleSubscribe = async () => {
    try {
      setIsLoading(true);
      
      if (!user) {
        // Store plan details in localStorage before redirecting to login
        localStorage.setItem('subscriptionIntent', JSON.stringify(plan));
        navigate('/auth', { state: { returnTo: '/subscribe' } });
        return;
      }

      console.log('Initiating checkout for plan:', plan);
      
      // Create checkout session with proper headers
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { 
          planDetails: plan,
          userId: user.id,
          customerEmail: user.email
        }
      });

      if (error) {
        console.error('Subscription error:', error);
        throw error;
      }

      if (!data?.url) {
        throw new Error('No checkout URL returned from server');
      }

      console.log('Redirecting to checkout URL:', data.url);
      // Redirect to Stripe checkout
      window.location.href = data.url;
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error("Failed to initiate subscription. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <AccessibilityWidget />
      <header>
        <MainNav currentPath="subscribe" />
      </header>
      
      <main className="container max-w-4xl mx-auto px-4 py-12">
        <Button asChild variant="ghost" className="mb-8">
          <Link to="/pricing">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Pricing
          </Link>
        </Button>

        <div className="bg-card rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold mb-6">Confirm Your Subscription</h1>
          
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h2 className="font-semibold mb-2">Plan Details</h2>
                <ul className="space-y-2 text-muted-foreground">
                  <li>Plan: {plan.title}</li>
                  <li>Billing: {plan.billingPeriod === 'yearly' ? 'Yearly' : 'Monthly'}</li>
                  <li>Price: ${plan.price}/{plan.billingPeriod === 'yearly' ? 'year' : 'month'}</li>
                  <li>Maximum Installations: {plan.maxInstallations}</li>
                </ul>
              </div>
              
              <div>
                <h2 className="font-semibold mb-2">What's Included</h2>
                <ul className="space-y-2 text-muted-foreground">
                  <li>Full accessibility widget</li>
                  <li>Voice navigation</li>
                  <li>Analytics dashboard</li>
                  <li>Priority support</li>
                </ul>
              </div>
            </div>

            <hr className="my-6" />

            <div className="text-sm text-muted-foreground">
              <p>By subscribing, you agree to our terms of service and privacy policy.</p>
              <p className="mt-2">You can cancel your subscription at any time from your account settings.</p>
            </div>

            <Button 
              size="lg" 
              className="w-full md:w-auto"
              onClick={handleSubscribe}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Proceed to Payment'}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SubscribePage;
